/** A bare number, carrying no unit of its own. */
const UNITLESS = /^[+-]?\d*\.?\d+$/;

/** Values that need no browser to resolve: a bare number, or a length already in pixels. */
const PIXELS = /^[+-]?\d*\.?\d+(?:px)?$/;

/**
 * Measures a CSS length by letting the browser resolve it, which is the only way to be correct for
 * units whose meaning depends on context (`em` against the host's font size, `%` against its width,
 * anything inside a `calc()`).
 *
 * @param {string} value - Any CSS length
 * @param {HTMLElement} [host] - Element the length resolves against
 * @returns {number} the resolved width in pixels
 */
function measure(value, host) {
  let parent = host?.isConnected ? host : document.body;

  if (!parent) return 0;

  let probe = document.createElement('div');

  // The probe stays in flow. Taking it out with `position:absolute` would resolve percentages
  // against the nearest positioned ancestor instead of the host, which is rarely the host and is
  // the viewport when nothing between them is positioned. It is empty and zero-height, and it is
  // removed before this function returns, so it never lands in a frame the user sees.
  probe.style.cssText = `visibility:hidden;pointer-events:none;height:0;width:${value};`;
  parent.appendChild(probe);

  let { width } = probe.getBoundingClientRect();

  probe.remove();

  return width;
}

/**
 * Resolves a CSS length to a pixel count, for values that have to reach JS positioning math rather
 * than a stylesheet. Anchored surfaces are the case in point: `@anchorOffset` and the tooltip's
 * `@offset` are authored as lengths so they can track type scale (`0.5rem`) or a container
 * (`2%`), but `utils/spds/positioning` adds them to `getBoundingClientRect` values in pixels.
 *
 * Pixel and unitless values resolve arithmetically. Anything else is measured against `element`,
 * which touches the DOM, so callers on a hot path should keep their authored values in `px`.
 *
 * @param {string|number} [value] - CSS length, or a bare number treated as pixels
 * @param {HTMLElement} [element] - Element the length resolves against, for context-dependent units
 * @returns {number} the length in pixels, or 0 when there is nothing to resolve
 */
export function resolveLength(value, element) {
  let trimmed = `${value ?? ''}`.trim();

  if (PIXELS.test(trimmed)) return parseFloat(trimmed);

  // Nothing left that the browser could resolve, including a number that was never a length.
  return trimmed ? measure(trimmed, element) : 0;
}

/**
 * Gives a bare number the unit CSS won't infer for it, for values headed to a stylesheet rather
 * than to JS math. A custom property is where this matters: `--gap: 8` parses happily and only
 * fails once something tries to use it as a length, taking the whole declaration down with it — so
 * a menu whose gap lost its unit doesn't open 8px too close, it loses its position entirely.
 *
 * Anything already carrying a unit, or any expression, is returned as authored for the browser to
 * resolve. Empty values stay empty, which `setProperty` treats as removing the declaration, so a
 * stylesheet fallback still applies.
 *
 * @param {string|number} [value] - CSS length, or a bare number treated as pixels
 * @returns {string} the value as a CSS length
 */
export function toCssLength(value) {
  let trimmed = `${value ?? ''}`.trim();

  return UNITLESS.test(trimmed) ? `${trimmed}px` : trimmed;
}

export default {
  resolveLength,
  toCssLength,
};
